import { RedoOutlined } from '@ant-design/icons';
import { Button, Checkbox, Flex, Tooltip } from 'antd';
import { type FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enhancedApi, type DialogShortData } from '@shared/api';
import { useNotify } from '@shared/hooks/use-notify';
import styles from './lead-actions.module.scss';

interface LeadActionsProps {
  selectedDialog: DialogShortData;
}

export const LeadActions: FC<LeadActionsProps> = ({ selectedDialog }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'dialogs.clientInfo' });
  const notify = useNotify();
  const activeClientIdRef = useRef(selectedDialog.client_id);
  const [isTest, setIsTest] = useState(selectedDialog.is_test);

  const [updateClientTest, { isLoading: isUpdatingTest }] =
    enhancedApi.useUpdateClientTestClientUpdateTestPostMutation();
  const [resendLead, { isLoading: isResendingLead }] =
    enhancedApi.useResendLeadClientLeadResendPostMutation();

  activeClientIdRef.current = selectedDialog.client_id;

  useEffect(() => {
    setIsTest(selectedDialog.is_test);
  }, [selectedDialog.client_id, selectedDialog.is_test]);

  const handleTestChange = async (nextIsTest: boolean) => {
    const clientId = selectedDialog.client_id;
    const previousIsTest = isTest;
    setIsTest(nextIsTest);

    try {
      await updateClientTest({
        updateClientTestData: {
          client_id: clientId,
          is_test: nextIsTest,
        },
      }).unwrap();
    } catch {
      if (activeClientIdRef.current === clientId) {
        setIsTest(previousIsTest);
      }
    }
  };

  const handleResendLead = async () => {
    try {
      await resendLead({ clientId: selectedDialog.client_id }).unwrap();
      notify({ type: 'success', message: t('leadResent') });
    } catch {
      // API errors are displayed by the shared RTK Query error handler.
    }
  };

  return (
    <Flex align="center" justify="space-between" gap={16} className={styles.root}>
      <Checkbox
        checked={isTest}
        disabled={isUpdatingTest}
        onChange={(event) => void handleTestChange(event.target.checked)}
      >
        {t('testLead')}
      </Checkbox>

      {selectedDialog.lead_created_at ? (
        <Tooltip title={t('resendLead')}>
          <Button
            type="text"
            size="small"
            icon={<RedoOutlined />}
            loading={isResendingLead}
            aria-label={t('resendLead')}
            className={styles.resendButton}
            onClick={() => void handleResendLead()}
          />
        </Tooltip>
      ) : null}
    </Flex>
  );
};
